// client/src/coms/views/canvas_parts/canvas_room_view_parts/canvasRoomLiveUtils.js
import { getPointerTiltOrientation } from './grabToolUtils';

export const CANVAS_LIVE_MAX_BRUSH_SIZE = 4096;

export function createLiveClientId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {}
  return 'canvas-live-' + Date.now() + '-' + Math.random().toString(16).slice(2);
}

export function createLiveStrokeId(userId) {
  const safeUserId = Number(userId) || 0;
  return 'stroke-' + safeUserId + '-' + Date.now() + '-' + Math.random().toString(16).slice(2);
}

export function getFitZoom(stageSize, viewportSize) {
  const stageWidth = Math.max(1, Number(stageSize && stageSize.width) || 1);
  const stageHeight = Math.max(1, Number(stageSize && stageSize.height) || 1);
  const viewportWidth = Math.max(1, (Number(viewportSize && viewportSize.width) || 1) - 20);
  const viewportHeight = Math.max(1, (Number(viewportSize && viewportSize.height) || 1) - 20);
  const fitZoom = Math.min(viewportWidth / stageWidth, viewportHeight / stageHeight);
  return Number.isFinite(fitZoom) && fitZoom > 0 ? fitZoom : 1;
}

export const LIVE_PEN_BRUSH_PRESETS = new Set([
  'pen',
  'paint',
  'pencil',
  'jitter_blob',
  'calligraphy',
  'airbrush',
  'imported-local-preview',
]);

function normalizeLiveBrushPresetBase(rawPreset) {
  const raw = String(rawPreset || 'pen').trim();
  const base = raw || 'pen';
  const normalized = base.toLowerCase().replace(/[\s_-]+/g, ' ').trim();
  if (
    !normalized
    || normalized === 'default'
    || normalized === 'default 1'
    || normalized === 'basic'
    || normalized === 'basic 1'
    || normalized === 'round'
    || normalized === 'round brush'
    || normalized === 'simple'
    || normalized === 'simple round'
    || normalized === 'brush'
    || normalized === 'pen brush'
  ) {
    return 'pen';
  }
  return base;
}

export function parseLiveBrushPreset(rawPreset) {
  const raw = String(rawPreset || 'pen').trim();
  const match = raw.match(/^([^:|]+)(?::j(\d+(?:\.\d+)?))?$/);
  const basePreset = normalizeLiveBrushPresetBase(match && match[1] ? match[1] : raw);
  return {
    basePreset,
    jitterAmount: match && match[2] ? Number(match[2]) : null,
  };
}

export function createOutgoingLiveBrushPreset(tool, brushPreset, jitterAmount) {
  const parsedPreset = parseLiveBrushPreset(brushPreset);
  const basePreset = parsedPreset.basePreset;
  if (tool === 'paint') return 'paint';
  if (tool === 'pen' && basePreset === 'jitter_blob') {
    const safeJitter = Math.max(0, Math.min(100, Math.round(Number(jitterAmount) || 0)));
    return `jitter_blob:j${safeJitter}`;
  }
  if (tool === 'pen' && basePreset === 'imported-local-preview') {
    return 'imported-local-preview';
  }
  return basePreset;
}

export function canBroadcastLiveStroke(tool, brushPreset) {
  const parsed = parseLiveBrushPreset(brushPreset);
  if (tool === 'pen') return LIVE_PEN_BRUSH_PRESETS.has(parsed.basePreset);
  if (tool === 'paint') return parsed.basePreset === 'paint';
  return tool === 'eraser' || tool === 'smudge' || tool === 'liquify';
}

export function clampLiveNumber(value, fallback, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

function clampLivePressure(value, fallback = null) {
  const pressure = Number(value);
  if (!Number.isFinite(pressure)) return fallback;
  return Math.max(0, Math.min(1, pressure));
}

function sanitizeLiveOrientation(orientation) {
  if (!orientation || typeof orientation !== 'object') return null;
  const azimuth = Number(orientation.azimuth);
  const elevation = Number(orientation.elevation);
  if (!Number.isFinite(azimuth) || !Number.isFinite(elevation)) return null;
  return {
    azimuth: Math.max(-360, Math.min(360, azimuth)),
    elevation: Math.max(0, Math.min(90, elevation)),
    detected: orientation.detected !== false,
  };
}

function copyFiniteNumber(target, source, key, min = null, max = null) {
  const value = Number(source && source[key]);
  if (!Number.isFinite(value)) return;
  let nextValue = value;
  if (Number.isFinite(min)) nextValue = Math.max(min, nextValue);
  if (Number.isFinite(max)) nextValue = Math.min(max, nextValue);
  target[key] = nextValue;
}

export function sanitizeLivePointInput(input) {
  if (!input || typeof input !== 'object') return null;

  const sanitized = {};
  const pointerType = typeof input.pointerType === 'string' ? input.pointerType.toLowerCase() : '';
  if (pointerType) sanitized.pointerType = pointerType;

  const pressure = clampLivePressure(input.pressure, null);
  if (pressure != null) sanitized.pressure = pressure;

  copyFiniteNumber(sanitized, input, 'tiltX', -90, 90);
  copyFiniteNumber(sanitized, input, 'tiltY', -90, 90);
  copyFiniteNumber(sanitized, input, 'twist', 0, 359);
  copyFiniteNumber(sanitized, input, 'tangentialPressure', -1, 1);
  copyFiniteNumber(sanitized, input, 'altitudeAngle', 0, Math.PI / 2);
  copyFiniteNumber(sanitized, input, 'azimuthAngle', -Math.PI * 2, Math.PI * 2);
  copyFiniteNumber(sanitized, input, 'buttons', 0, 31);

  if (typeof input.rightButtonDown === 'boolean') sanitized.rightButtonDown = input.rightButtonDown;
  if (typeof input.calligraphyFollowFlow === 'boolean') sanitized.calligraphyFollowFlow = input.calligraphyFollowFlow;

  const directOrientation = sanitizeLiveOrientation(input.pencilOrientation);
  const tiltOrientation = directOrientation || sanitizeLiveOrientation(getPointerTiltOrientation({ ...sanitized, pointerType: sanitized.pointerType || pointerType || 'pen' }));
  if (tiltOrientation) sanitized.pencilOrientation = tiltOrientation;

  return Object.keys(sanitized).length ? sanitized : null;
}

export function mergeLivePointInput(point, fallbackInput) {
  const safePoint = point && typeof point === 'object' ? point : null;
  const safeInput = sanitizeLivePointInput(fallbackInput);
  if (!safePoint && !safeInput) return null;
  return {
    ...(safeInput || {}),
    ...(safePoint || {}),
    pencilOrientation: sanitizeLiveOrientation((safePoint && safePoint.pencilOrientation) || (safeInput && safeInput.pencilOrientation)) || undefined,
  };
}

export function sanitizeLivePoint(point) {
  if (!point || typeof point !== 'object') return null;
  const x = Number(point.x);
  const y = Number(point.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

  const pointInput = sanitizeLivePointInput(point) || {};
  const sanitized = { x, y, ...pointInput };

  const pressure = clampLivePressure(point.pressure, null);
  if (pressure != null) {
    sanitized.pressure = Math.max(0.02, pressure);
  }

  const orientation = sanitizeLiveOrientation(point.pencilOrientation) || sanitizeLiveOrientation(pointInput.pencilOrientation);
  if (orientation) {
    sanitized.pencilOrientation = orientation;
  }

  return sanitized;
}

const pendingLiveStrokePoints = new Map();
let pendingLiveStrokeFlushTimer = null;

const structuralLiveEventQueues = new Map();

function waitForLiveRetry(delayMs) {
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
      window.setTimeout(resolve, delayMs);
      return;
    }
    setTimeout(resolve, delayMs);
  });
}


function normalizeCanvasLiveSyncPayload(payload) {
  const source = payload && typeof payload === 'object' ? payload : {};
  const rawType = String(source.type || '').trim();
  const isSyncEventType = rawType.startsWith('canvas_sync_') || rawType.startsWith('layer_sync_');
  if (!isSyncEventType) {
    return source;
  }

  const state = source.state && typeof source.state === 'object' ? source.state : {};
  const maskAction = String(source.maskAction || state.maskAction || rawType).trim() || rawType;
  const fallbackReason = rawType.startsWith('layer_sync_')
    ? 'layer_sync:' + rawType.slice('layer_sync_'.length)
    : 'canvas_sync:' + rawType.slice('canvas_sync_'.length);
  const reason = String(source.reason || state.reason || fallbackReason).trim();

  const normalized = {
    ...source,
    type: 'mask_info',
    maskAction,
    reason,
    state: {
      ...state,
      originalEventType: rawType,
      maskAction,
      reason,
    },
  };


  return normalized;
}

async function sendCanvasLiveStrokeEventNow(canvasId, payload, retryCount = 0) {
  const safeCanvasId = Number(canvasId);
  if (!Number.isInteger(safeCanvasId) || safeCanvasId <= 0) return false;
  if (!payload || typeof payload !== 'object') return false;

  const url = '/api/canvas-live/' + encodeURIComponent(String(safeCanvasId)) + '/stroke';
  let body = '';

  try {
    body = JSON.stringify(payload);
  } catch {
    return false;
  }

  const attempts = Math.max(1, Number(retryCount) + 1);
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
        keepalive: false,
      });
      let responseText = '';
      try {
        if (response && typeof response.text === 'function') responseText = await response.text();
      } catch {}

      if (response && response.ok) {
        return true;
      }

    } catch {
    }

    if (attempt < attempts - 1) {
      await waitForLiveRetry(120 * (2 ** attempt));
    }
  }

  return false;
}

function isStructuralCanvasEvent(payload) {
  const type = String(payload && payload.type ? payload.type : '').trim();
  if (type === 'layer_state') return true;
  if (type !== 'mask_info') return false;
  const maskInfo = payload && payload.maskInfo && typeof payload.maskInfo === 'object'
    ? payload.maskInfo
    : null;
  const state = payload && payload.state && typeof payload.state === 'object'
    ? payload.state
    : null;
  const action = String(
    payload.maskAction
    || (state && state.maskAction)
    || (maskInfo && (maskInfo.action || maskInfo.maskAction))
    || ''
  ).trim();
  return action === 'layer_delete' || action === 'layer_create';
}

function enqueueStructuralCanvasEvent(canvasId, payload) {
  const safeCanvasId = Number(canvasId);
  const clientId = String(payload && payload.clientId ? payload.clientId : '');
  const queueKey = `${safeCanvasId}:${clientId}`;
  const previous = structuralLiveEventQueues.get(queueKey) || Promise.resolve();
  const next = previous
    .catch(() => false)
    .then(() => sendCanvasLiveStrokeEventNow(safeCanvasId, payload, 3));
  structuralLiveEventQueues.set(queueKey, next);
  next.finally(() => {
    if (structuralLiveEventQueues.get(queueKey) === next) {
      structuralLiveEventQueues.delete(queueKey);
    }
  });
  return next;
}

function createPendingLiveStrokePointKey(canvasId, payload) {
  return [
    Number(canvasId) || 0,
    String(payload && payload.clientId ? payload.clientId : ''),
    String(payload && payload.strokeId ? payload.strokeId : ''),
  ].join(':');
}

function flushPendingLiveStrokePoints() {
  if (pendingLiveStrokeFlushTimer != null && typeof window !== 'undefined') {
    window.clearTimeout(pendingLiveStrokeFlushTimer);
  }
  pendingLiveStrokeFlushTimer = null;

  const queued = Array.from(pendingLiveStrokePoints.values());
  pendingLiveStrokePoints.clear();
  queued.forEach(({ canvasId, payload }) => {
    sendCanvasLiveStrokeEventNow(canvasId, payload);
  });
}

export function postCanvasLiveStrokeEvent(canvasId, payload) {
  const safeCanvasId = Number(canvasId);
  if (!Number.isInteger(safeCanvasId) || safeCanvasId <= 0) return Promise.resolve(false);
  if (!payload || typeof payload !== 'object') return Promise.resolve(false);

  const normalizedPayload = normalizeCanvasLiveSyncPayload(payload);
  const eventType = String(normalizedPayload.type || '').trim();

  if (eventType === 'stroke_point') {
    const key = createPendingLiveStrokePointKey(safeCanvasId, normalizedPayload);
    pendingLiveStrokePoints.set(key, { canvasId: safeCanvasId, payload: normalizedPayload });

    if (pendingLiveStrokeFlushTimer == null && typeof window !== 'undefined') {
      pendingLiveStrokeFlushTimer = window.setTimeout(flushPendingLiveStrokePoints, 24);
    }
    return Promise.resolve(true);
  }

  if (eventType === 'stroke_end' || eventType === 'raster_patch') {
    flushPendingLiveStrokePoints();
  }

  if (eventType === 'raster_patch') {
    return enqueueStructuralCanvasEvent(safeCanvasId, normalizedPayload);
  }

  if (isStructuralCanvasEvent(normalizedPayload)) {
    return enqueueStructuralCanvasEvent(safeCanvasId, normalizedPayload);
  }

  return sendCanvasLiveStrokeEventNow(safeCanvasId, normalizedPayload, 0);
}

export function getCanvasToolSettingsKey(tool, brushPreset) {
  if (tool === 'pen') {
    return typeof brushPreset === 'string' && brushPreset ? brushPreset : 'pen';
  }

  return typeof tool === 'string' && tool ? tool : 'pen';
}
